import React, { useState } from 'react'
import { useEscalatedIssuesPolling } from 'components/layout/Header/components/IssuesContainer/hooks/useEscalatedIssuesPolling'
import { useNotificationsPolling } from 'components/layout/Header/components/NotificationsContainer/hooks'
import { makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { IUserDetailsHook, useUserDetails } from 'utils/hooks'
import NavigationMobileDrawer from '../NavigationMobileDrawer'
import ApiLink from './components/ApiLink'
import EnvironmentLink from './components/EnvironmentLink'
import Menu from './components/Menu'
import NavLogo from './components/NavLogo'
import NavigationToggleButton from './components/NavigationToggleButton'
import { INavigationRoute } from './types'
import { generateDefaultNewRoutes } from './utils/generateDefaultNewRoutes'
import { generateDefaultRoutes, INotificationsOptions } from './utils/generateDefaultRoutes'

const NAVBAR_HEIGHT_PX = 48

const useNavigationBarStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    width: '100%',
    height: NAVBAR_HEIGHT_PX,
    backgroundColor: brandColors.coolGray8,
    paddingLeft: 0,
    paddingRight: 0,

    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
    },
  },
  innerContainer: {
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  logoContainer: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    flex: '0 0 auto',
    paddingLeft: theme.spacing(1.5),
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    flexShrink: 0,
    flexGrow: 0,
    flexBasis: 'auto',
    mr: 0,
  },
  linkContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 auto',
    marginLeft: theme.spacing(1.5),
    color: brandColors.coolGray8,

    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
      alignItems: 'center',
      marginLeft: theme.spacing(2),
    },
  },
  routeContainer: {
    height: '100%',
    display: 'flex',
    flex: '1 1 100%',
    justifyContent: 'flex-end',
  },
  environmentLink: {
    color: brandColors.gold2,
  },
  apiLink: {
    color: brandColors.skyBlue6,
  },
}))

const getRoutes = (
  routesProp: INavigationRoute[],
  options: IUserDetailsHook,
  notifications: INotificationsOptions,
  isMpNavigationBarUpdateEnabled: boolean
): INavigationRoute[] => {
  if (routesProp.length) {
    return routesProp
  }

  const { userPermissions } = options || {}

  if (isMpNavigationBarUpdateEnabled) {
    return generateDefaultNewRoutes(userPermissions, notifications)
  }

  return generateDefaultRoutes(userPermissions, notifications)
}
export interface INavigationBarProps {
  routes?: INavigationRoute[]
  /**
   * @default false
   */
  isOpen?: boolean
}

const NavigationBar = ({
  isOpen: isOpenProp = false,
  routes: routesProp = [],
}: INavigationBarProps) => {
  const isMpNavigationBarUpdateEnabled = useFeatureFlag('mpNavigationBarUpdate')
  const userDetails = useUserDetails()
  const { userPermissions } = userDetails || {}
  const [isOpen, setOpen] = useState<boolean>(isOpenProp)

  const { unreadCount } = useNotificationsPolling()
  const { issueCount } = useEscalatedIssuesPolling()

  /**
   * Only generate legacy routes
   */
  const desktopRoutes = getRoutes(
    routesProp,
    userDetails,
    {
      notifications: unreadCount,
      escalatedIssues: issueCount,
    },
    false
  )

  /**
   * When flag is on, generate new "mobile" routes
   */
  const mobileRoutes = getRoutes(
    routesProp,
    userDetails,
    {
      notifications: unreadCount,
      escalatedIssues: issueCount,
    },
    isMpNavigationBarUpdateEnabled
  )

  const filteredDesktopRoutes = desktopRoutes.filter(route => !!route.display)
  const filteredMobileRoutes = mobileRoutes.filter(route => !!route.display)

  const classes = useNavigationBarStyles()

  const toggleHandler = () => setOpen(prev => !prev)
  const closeHandler = () => setOpen(false)

  return (
    <nav data-test='NavigationBar' className={classes.root}>
      <div className={classes.innerContainer}>
        <div className={classes.logoContainer}>
          <div className={classes.logo}>
            <a aria-label='Transfix Home' href={__ROOT__}>
              <NavLogo />
            </a>
          </div>
          <div className={classes.linkContainer}>
            <EnvironmentLink />
            <ApiLink />
          </div>
        </div>
        <Menu routes={filteredDesktopRoutes} className={classes.routeContainer} />
        {userPermissions && <NavigationToggleButton isOpen={isOpen} onClick={toggleHandler} />}
        <NavigationMobileDrawer
          routes={filteredMobileRoutes}
          isOpen={isOpen}
          onClose={closeHandler}
        />
      </div>
    </nav>
  )
}

export default NavigationBar
